/** Advanced catalog filter metadata — ids must match `src/lib/catalog-advanced-filters.ts`; labels are UI-only. */

import { ITEM_FORMAT_OPTIONS, ITEM_TYPE_OPTIONS } from './lib/itemTaxonomy.js';

export const CATALOG_ADV_FIELD_OPTIONS = [
  { id: 'name', label: 'Title' },
  { id: 'status', label: 'Status' },
  { id: 'stage', label: 'Stage' },
  { id: 'content_access', label: 'Content access' },
  { id: 'internal_publisher', label: 'Who we publish as' },
  { id: 'description', label: 'Description' },
  { id: 'short_description', label: 'Short blurb' },
  { id: 'web_synopsis', label: 'Web summary' },
  { id: 'genres', label: 'Genres' },
  { id: 'visibility', label: 'Visibility' },
  { id: 'imprints', label: 'Imprints' },
  { id: 'bisac', label: 'BISAC codes' },
  { id: 'tags', label: 'Tags' },
  { id: 'keywords', label: 'Keywords' },
  { id: 'country_codes', label: 'Country availability' },
  { id: 'contributors', label: 'Cast & credits' },
  { id: 'publishers', label: 'Publishers on the listing' },
  { id: 'image', label: 'Cover art' },
  { id: 'playable_audio', label: 'Listenable runtime' },
  { id: 'isbn13', label: 'ISBN' },
  { id: 'book_id', label: 'Book code' },
  { id: 'series_id', label: 'Series' },
  { id: 'group_id', label: 'Collection / set' },
  { id: 'slug', label: 'Link name (URL)' },
  { id: 'release_at', label: 'Release date' },
  { id: 'preorder_release', label: 'Preorder date' },
  { id: 'original_release', label: 'Original release date' },
  { id: 'sample_url', label: 'Sample clip link' },
  { id: 'format', label: 'Format' },
  { id: 'type', label: 'Product type' },
  { id: 'language', label: 'Language' },
  { id: 'edition', label: 'Edition' },
  { id: 'price', label: 'Price' },
  { id: 'total_duration', label: 'Runtime length' },
  { id: 'free', label: 'Free title' },
  { id: 'purchasable', label: 'Available to buy' },
  { id: 'featured_on_app', label: 'Featured in the app' },
  { id: 'has_96k_audio', label: 'Has 96k audio' },
  { id: 'has_192k_audio', label: 'Has 192k audio' },
  { id: 'show_release_date_on_preorder', label: 'Shows release date on preorder' },
  { id: 'is_archived', label: 'Archived' },
] as const;

export const CATALOG_ADV_FIELD_ID_SET = new Set<string>(CATALOG_ADV_FIELD_OPTIONS.map((f) => f.id));

export const CATALOG_ADV_OP_OPTIONS = [
  { id: 'eq', label: 'Is exactly' },
  { id: 'neq', label: 'Is not' },
  { id: 'contains', label: 'Includes' },
  { id: 'empty', label: 'Is blank' },
  { id: 'not_empty', label: 'Is filled in' },
] as const;

export const CATALOG_ADV_OP_ID_SET = new Set<string>(CATALOG_ADV_OP_OPTIONS.map((o) => o.id));

/**
 * Known picklist values for "Is exactly" / "Is not" on fields with a closed set of values.
 * `stage` + `content_access` value sets confirmed against live mirror data (Sep 2026) —
 * expand here if Iglu ever adds a new value; unlisted fields fall back to free text.
 */
export const CATALOG_ADV_ENUM_OPTIONS: Record<string, Array<{ value: string; label: string }>> = {
  status: [
    { value: 'draft', label: 'Draft' },
    { value: 'published', label: 'Published' },
    { value: 'archived', label: 'Archived' },
  ],
  stage: [
    { value: 'draft', label: 'Draft' },
    { value: 'ready', label: 'Ready' },
    { value: 'archived', label: 'Archived' },
  ],
  content_access: [
    { value: 'availableForPurchase', label: 'Available for purchase' },
    { value: 'notForSale', label: 'Not for sale' },
    { value: 'noLongerViewable', label: 'No longer viewable' },
  ],
  type: ITEM_TYPE_OPTIONS,
  format: ITEM_FORMAT_OPTIONS.filter((o) => o.value !== ''),
  // Visibility is a multi-value tag array — closed set confirmed against
  // src/lib/visibility-diagnostics.ts (sbtDirect included for filtering even
  // though it doesn't affect app visibility itself).
  visibility: [
    { value: 'showOnApp', label: 'Show On App' },
    { value: 'audibleOnly', label: 'Audible Exclusive' },
    { value: 'disablePurchases', label: 'Purchases Disabled' },
    { value: 'sbtDirect', label: 'SBT Direct' },
  ],
};
